import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import { X, Image as ImageIcon, Send, Camera, Film, Trash2, FileText, Music2, Smile } from "lucide-react";
import { FILTER_KEYS, FILTER_PRESETS, filterCss, STICKER_GROUPS } from "@/lib/mediaEffects";
import MusicPicker from "@/components/feed/MusicPicker";
// iter161 · ReelComposer entfernt — Reels-Kategorie abgeschafft, Video hängt jetzt am regulären Post.
import UploadingBadge from "@/components/common/UploadingBadge";
import RichTextEditor from "@/components/common/RichTextEditor";
import ImageCropDialog from "@/components/home/ImageCropDialog";
import { AnonymizeDialog } from "@/components/media/AnonymizeDialog";
import { AnonymityNotice } from "@/components/media/AnonymityNotice";
import AIAssistBar from "@/components/ai/AIAssistBar";
import MediaAnalyseChip from "@/components/media/MediaAnalyseChip";
import MonetizationModeSelector from "@/components/common/MonetizationModeSelector";
// iter238 (Variante B §5) · Publish-Consent Pflicht für SINGLE_BUY/PPV.
import PublishConsentCheckbox from "@/components/paid-media/PublishConsentCheckbox";
import ListingAttachPicker from "@/components/common/ListingAttachPicker";
import MediaEffectsEditor from "@/components/feed/MediaEffectsEditor";

// iter324 · Handy-Uploads (iPhone HEIC / sehr große Fotos) scheitern am Origin (Cloudflare 520).
// Bilder clientseitig auf max. 1920px verkleinern und als JPEG re-encodieren → kleine, kompatible Datei.
async function downscaleImage(file, maxEdge = 1920, quality = 0.85) {
  if (!file || !(file.type || "").startsWith("image/")) return file;
  try {
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = dataUrl;
    });
    const w0 = img.naturalWidth || img.width;
    const h0 = img.naturalHeight || img.height;
    if (!w0 || !h0) return file;
    const scale = Math.min(1, maxEdge / Math.max(w0, h0));
    const w = Math.round(w0 * scale);
    const h = Math.round(h0 * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob) return file;
    const base = (file.name || "image").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file; // Fallback: Originaldatei (z. B. Browser kann HEIC nicht decodieren)
  }
}

export default function FeedComposer() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null); // null | "post" | "story"
  const [initialPollMode, setInitialPollMode] = useState(false);
  const [editStory, setEditStory] = useState(null); // iter320 · Story-Bearbeiten (vorbefüllte Slides)
  // P2/P4 Bereichstrennung · Kontext des Composers: SOCIAL (Default) | STILLTOUCHEDX
  const [area, setArea] = useState("SOCIAL");

  // iter309 · Wizard-Rücksprung: Nach dem Publizieren navigiert der Wizard auf "/" und
  // setzt sessionStorage-Flags. Beim Mount (bzw. Re-Mount auf "/") öffnen wir den
  // Composer automatisch im richtigen Bereich — timing-unabhängig vom Event. Der
  // eigentliche Entwurf wird dann in PostComposer (korrekter Scope) beim Mount restauriert.
  useEffect(() => {
    try {
      if (sessionStorage.getItem("feed_post_draft_restore") === "1") {
        const storedArea = sessionStorage.getItem("feed_post_draft_area") === "STILLTOUCHEDX" ? "STILLTOUCHEDX" : "SOCIAL";
        setArea(storedArea);
        setMode("post");
        setOpen(true);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (!user || user === false) { navigate("/login"); return; }
      const requested = e?.detail?.mode;
      const withPoll = !!e?.detail?.poll;
      const reqArea = e?.detail?.area === "STILLTOUCHEDX" ? "STILLTOUCHEDX" : "SOCIAL";
      setArea(reqArea);
      setEditStory(e?.detail?.editStory || null);
      setOpen(true);
      // Phase 3 · Story jetzt AUCH in stilltouchedX (strikt über STX-Ablage + stx_profiles-Identität getrennt).
      setMode(requested && ["post", "story"].includes(requested) ? requested : null);
      setInitialPollMode(withPoll);
    };
    window.addEventListener("open-feed-composer", handler);
    return () => window.removeEventListener("open-feed-composer", handler);
  }, [user, navigate]);

  // Externes Schließen (z. B. beim Wechsel in den Artikel-Wizard aus dem Artikel-Picker).
  useEffect(() => {
    const close = () => { setOpen(false); setMode(null); setInitialPollMode(false); setEditStory(null); };
    window.addEventListener("close-feed-composer", close);
    return () => window.removeEventListener("close-feed-composer", close);
  }, []);

  const closeAll = () => { setOpen(false); setMode(null); setInitialPollMode(false); setEditStory(null); };
  const notifyPublished = () => {
    // iter311 · Nach dem Veröffentlichen in den PASSENDEN Feed springen.
    // iter320 · Beim BEARBEITEN aus dem Studio NICHT wegnavigieren — Studio>Stories bleibt offen.
    const wasEdit = !!editStory;
    const targetArea = area;
    closeAll();
    window.dispatchEvent(new CustomEvent("feed-refresh"));
    window.dispatchEvent(new CustomEvent("feed-post-published"));
    if (!wasEdit) navigate(targetArea === "STILLTOUCHEDX" ? "/stilltouchedx" : "/");
  };

  if (!open) return null;

  return (
    <>

      {/* Bottom-Sheet with mode picker */}
      {open && mode === null && (
        <div
          data-testid="feed-composer-sheet"
          className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center"
          onClick={closeAll}
        >
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-2xl pb-4 pt-2 sm:pt-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-[#E5E5E5]">
              <p className="text-sm font-semibold text-[#0A0A0A]">{t("feed.composer_share_prompt")}</p>
              <button data-testid="feed-composer-close" onClick={closeAll} aria-label={t("feed.composer_close")}
                className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-[#F5F5F5]">
                <X size={18} className="text-[#666]" />
              </button>
            </div>

            <div className="p-2 space-y-1">
              <SheetOption
                testid="feed-composer-pick-post"
                icon={<FileText size={22} className="text-[#0A0A0A]" />}
                title={t("feed.composer_pick_post_title")}
                subtitle={t("feed.composer_pick_post_sub")}
                onClick={() => setMode("post")}
              />
              {(
                <SheetOption
                  testid="feed-composer-pick-story"
                  icon={<Camera size={22} className="text-[#0A0A0A]" />}
                  title={t("feed.composer_pick_story_title")}
                  subtitle={t("feed.composer_pick_story_sub")}
                  onClick={() => setMode("story")}
                />
              )}
              {/* iter161 · Reels-Option entfernt — Posts unterstützen jetzt Bild+Video */}
            </div>
          </div>
        </div>
      )}

      {/* Post composer modal */}
      {mode === "post" && (
        <PostComposer
          key={`post-${initialPollMode ? "poll" : "plain"}`}
          onClose={closeAll}
          onDone={notifyPublished}
          initialPollMode={initialPollMode}
          area={area}
        />
      )}

      {/* Story composer modal */}
      {mode === "story" && (
        <StoryComposer key={editStory ? `edit-${editStory.story_id}` : "new"} onClose={closeAll} onDone={notifyPublished} area={area} editStory={editStory} />
      )}

      {/* iter161 · Reel-Composer entfernt (Reels-Kategorie abgeschafft) */}
    </>
  );
}

function SheetOption({ testid, icon, title, subtitle, onClick, disabled, badge }) {
  return (
    <button
      data-testid={testid}
      disabled={disabled}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-3 text-left rounded-2xl transition-colors ${
        disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-[#FAFAFA] active:bg-[#F5F5F5]"
      }`}
    >
      <div className="w-10 h-10 flex items-center justify-center bg-[#F5F5F5] rounded-full shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-[#0A0A0A]">{title}</p>
          {badge}
        </div>
        <p className="text-xs text-[#666] mt-0.5">{subtitle}</p>
      </div>
    </button>
  );
}

function PostComposer({ onClose, onDone, initialPollMode = false, area = "SOCIAL" }) {
  const { t } = useTranslation();
  // iter160 · Max Video-Länge Post = 1200s (User-Wunsch, für Vlogs/Longform-Content)
  const MAX_POST_VIDEO_SEC = 1200;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaType, setMediaType] = useState("IMAGE"); // "IMAGE" | "VIDEO"
  const [pendingFile, setPendingFile] = useState(null);
  // Phase 3 · Beitrags-Effekte + Musik (Playback-Metadaten)
  const [effects, setEffects] = useState({ filter: "none", speed: 1, texts: [], stickers: [] });
  const [music, setMusic] = useState(null);
  // iter165 · Bild-Ausschnitt-Dialog (wie beim Profilbild)
  const [cropFile, setCropFile] = useState(null);
  // iter173f · Bild läuft nach dem Zuschnitt durch die Anonymisierungs-Engine
  const [anonPostFile, setAnonPostFile] = useState(null);
  const [visibleTiers, setVisibleTiers] = useState([]);
  // iter134: Monetization
  const [monetizationMode, setMonetizationMode] = useState("FREE");
  const [priceEur, setPriceEur] = useState("");
  // iter238 (Variante B §5) · Publish-Consent Pflicht für SINGLE_BUY/PPV.
  const [publishConsent, setPublishConsent] = useState(false);
  const [attachedListingIds, setAttachedListingIds] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  // iter150 (2026-02-24) · Umfrage-Support
  const [pollEnabled, setPollEnabled] = useState(initialPollMode);
  const [pollOptions, setPollOptions] = useState(initialPollMode ? ["", ""] : []);
  const [pollDurationH, setPollDurationH] = useState(24);
  const pollPanelRef = useRef(null);

  // iter151-fix (2026-02-24) · initialPollMode kann sich zur Laufzeit ändern
  useEffect(() => {
    if (initialPollMode) {
      setPollEnabled(true);
      setPollOptions((prev) => (prev.length >= 2 ? prev : ["", ""]));
    }
  }, [initialPollMode]);

  // Wizard-Rücksprung: Entwurf sichern, wenn aus dem Composer in den Artikel-Wizard
  // gewechselt wird (Event aus ListingAttachPicker).
  useEffect(() => {
    const saver = () => {
      try {
        sessionStorage.setItem("feed_post_draft", JSON.stringify({
          title, description, mediaUrl, mediaType, visibleTiers,
          monetizationMode, priceEur, attachedListingIds,
          pollEnabled, pollOptions, pollDurationH,
        }));
      } catch { /* ignore */ }
    };
    window.addEventListener("composer-save-draft", saver);
    return () => window.removeEventListener("composer-save-draft", saver);
  }, [title, description, mediaUrl, mediaType, visibleTiers, monetizationMode, priceEur, attachedListingIds, pollEnabled, pollOptions, pollDurationH]);

  // Beim erneuten Öffnen nach Wizard-Rücksprung: Entwurf wiederherstellen und den
  // neu erstellten Artikel automatisch verlinken. iter309 · PostComposer mountet frisch,
  // sobald der Composer geöffnet wird (FeedComposer: `if (!open) return null`), daher
  // genügt ein Mount-Restore in DIESER (der korrekten) Komponente.
  function restoreDraftIfPending() {
    try {
      if (sessionStorage.getItem("feed_post_draft_restore") !== "1") return;
      const raw = sessionStorage.getItem("feed_post_draft");
      if (raw) {
        const d = JSON.parse(raw);
        setTitle(d.title || "");
        setDescription(d.description || "");
        setMediaUrl(d.mediaUrl || "");
        setMediaType(d.mediaType || "IMAGE");
        setVisibleTiers(d.visibleTiers || []);
        setMonetizationMode(d.monetizationMode || "FREE");
        setPriceEur(d.priceEur || "");
        setPollEnabled(!!d.pollEnabled);
        setPollOptions(d.pollOptions || []);
        setPollDurationH(d.pollDurationH || 24);
        let ids = Array.isArray(d.attachedListingIds) ? d.attachedListingIds : [];
        const pending = sessionStorage.getItem("feed_post_pending_attach");
        if (pending && !ids.includes(pending)) ids = [...ids, pending].slice(0, 3);
        setAttachedListingIds(ids);
      }
    } catch { /* ignore */ }
    finally {
      sessionStorage.removeItem("feed_post_draft_restore");
      sessionStorage.removeItem("feed_post_draft");
      sessionStorage.removeItem("feed_post_pending_attach");
      sessionStorage.removeItem("feed_post_draft_area");
    }
  }

  // Beim Mount einmalig prüfen (PostComposer mountet frisch beim Öffnen).
  useEffect(() => {
    restoreDraftIfPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // iter159 · Auto-Scroll nach Modal-Animation-Ende (statt hardcoded 250ms).
  // requestAnimationFrame läuft nach dem ersten Paint → Modal ist dann sicher aufgeklappt.
  useEffect(() => {
    if (!initialPollMode) return;
    let cancelled = false;
    const raf1 = requestAnimationFrame(() => {
      if (cancelled) return;
      requestAnimationFrame(() => {
        if (cancelled || !pollPanelRef.current) return;
        pollPanelRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
    };
  }, [initialPollMode]);

  // iter161 · Vereinheitlichter „Bild/Video"-Upload: akzeptiert beides, Video-Duration wird
  //           per Mime-Type-Sniff geprüft (max 1200s). Native Picker öffnet Kamera/Galerie/Dateien.
  // iter165 · Für Bilder öffnet sich vorher der Ausschnitt-Dialog (wie beim Profilbild-Upload).
  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setError("");
    const isVideo = (file.type || "").startsWith("video/");
    if (isVideo) {
      setUploading(true);
      try {
        const dur = await probePostVideoDuration(file);
        if (dur == null) { setError(t("feed.video_read_failed", "Video konnte nicht gelesen werden.")); return; }
        if (dur > MAX_POST_VIDEO_SEC + 0.5) {
          setError(t("feed.video_too_long", { sec: Math.round(dur), max: MAX_POST_VIDEO_SEC, defaultValue: `Video zu lang (${Math.round(dur)}s). Max ${MAX_POST_VIDEO_SEC}s (20 Min).` }));
          return;
        }
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await api.post(`/upload/video?area_scope=${area}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMediaUrl(`/api/files/${data.path}`);
        setMediaType("VIDEO");
        setPendingFile(null);
      } catch (err) {
        setError(formatError(err) || t("feed.upload_failed_retry", { defaultValue: "Upload fehlgeschlagen. Bitte erneut versuchen oder eine kleinere Datei (JPG/PNG/MP4) wählen." }));
      } finally {
        setUploading(false);
      }
    } else {
      // KEIN 16:9-Pre-Crop mehr: Vollbild geht an Qwen/Anonymisierung (wie KI-Labor).
      // Der Feed zeigt Bilder via object-contain → kein Zuschnitt fürs Anzeigen nötig.
      setAnonPostFile(await downscaleImage(file));
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setError("");
    setAnonPostFile(await downscaleImage(file));
  };

  // iter160 · Video-Upload (max 1200s Länge).
  const probePostVideoDuration = (file) => new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { const d = v.duration; window.URL.revokeObjectURL(v.src); resolve(d); };
    v.onerror = () => resolve(null);
    v.src = URL.createObjectURL(file);
  });
  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const dur = await probePostVideoDuration(file);
      if (dur == null) { setError(t("feed_composer.video_read_error")); return; }
      if (dur > MAX_POST_VIDEO_SEC + 0.5) {
        setError(`Video zu lang (${Math.round(dur)}s). Max ${MAX_POST_VIDEO_SEC}s (20 Min) pro Beitrag.`);
        return;
      }
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/upload/video?area_scope=${area}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMediaUrl(`/api/files/${data.path}`);
      setMediaType("VIDEO");
      setPendingFile(null);
    } catch (err) {
      setError(formatError(err) || "Video-Upload fehlgeschlagen");
    } finally { setUploading(false); }
  };

  const applyOptimizedFile = async (optimizedFile) => {
    setPendingFile(optimizedFile);
    setUploading(true); setError("");
    const formData = new FormData();
    formData.append("file", optimizedFile);
    try {
      const { data } = await api.post(`/upload?area_scope=${area}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMediaUrl(`/api/files/${data.path}`);
      setMediaType("IMAGE");
    } catch (err) { setError(formatError(err) || t("feed.composer_upload_failed")); }
    finally { setUploading(false); }
  };

  const publish = async () => {
    // iter161 · Kein Title-Feld mehr — Beschreibung ist neue Pflicht (weiter unten geprüft).
    // iter134 client-side validation
    if ((monetizationMode === "SINGLE_BUY" || monetizationMode === "PPV") && (!priceEur || Number(priceEur) <= 0)) {
      setError(t("feed_composer.price_required"));
      return;
    }
    if ((monetizationMode === "SUBSCRIBERS_ONLY" || monetizationMode === "PPV") && (!visibleTiers || visibleTiers.length === 0)) {
      setError(t("feed_composer.tier_required"));
      return;
    }
    // iter238 (Variante B §5) · Publish-Consent Pflicht bei kostenpflichtigen Posts.
    if ((monetizationMode === "SINGLE_BUY" || monetizationMode === "PPV") && !publishConsent) {
      setError(
        t("paid_media.publish_consent_required",
          { defaultValue: "Bitte bestätige, dass bereits heruntergeladene Kopien nicht zurückgerufen werden können." })
      );
      return;
    }
    // iter161 · Description ist Pflichtfeld (Titel entfernt, da im Feed nicht mehr sichtbar).
    const plainDesc = (description || "").replace(/<[^>]+>/g, "").trim();
    if (!plainDesc && !pollEnabled) {
      setError(t("feed.description_required", "Beschreibung ist erforderlich."));
      return;
    }
    // iter150: Poll-Validierung
    let pollPayload;
    if (pollEnabled) {
      const cleanOpts = pollOptions.map((o) => o.trim()).filter(Boolean);
      if (cleanOpts.length < 2) {
        setError("Umfrage: Mindestens 2 Optionen erforderlich");
        return;
      }
      if (cleanOpts.length > 6) {
        setError("Umfrage: Maximal 6 Optionen erlaubt");
        return;
      }
      pollPayload = { options: cleanOpts, duration_hours: Number(pollDurationH) || 24 };
    }
    setError(""); setPublishing(true);
    try {
      await api.post("/studio/social/posts", {
        // iter161 · Kein Title mehr im Composer — Backend erlaubt leeren Titel.
        title: "",
        description: description || undefined,
        media_url: mediaUrl || undefined,
        media_type: mediaUrl ? mediaType : "IMAGE",
        visible_tiers: visibleTiers,
        // iter134
        monetization_mode: monetizationMode,
        area_scope: area,
        price_eur: (monetizationMode === "SINGLE_BUY" || monetizationMode === "PPV") ? Number(priceEur) : undefined,
        // iter238 · Publish-Consent-Marker (Blueprint §5).
        publish_consent: (monetizationMode === "SINGLE_BUY" || monetizationMode === "PPV") ? true : undefined,
        attached_listing_ids: attachedListingIds.length > 0 ? attachedListingIds : undefined,
        // iter150
        poll: pollPayload,
        // Phase 3 · Effekte + Musik (nur wenn Media vorhanden)
        effects: mediaUrl ? effects : undefined,
        music: mediaUrl ? (music || undefined) : undefined,
      });
      onDone();
    } catch (err) { setError(formatError(err) || t("feed.composer_publish_failed")); }
    finally { setPublishing(false); }
  };

  return (
    <>
    <div data-testid="feed-composer-post" className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl shadow-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
          <p className="text-sm font-semibold text-[#0A0A0A]">{t("feed.composer_post_title")}</p>
          <button data-testid="feed-composer-post-close" onClick={onClose} aria-label={t("feed.composer_close")}
            className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-[#F5F5F5]">
            <X size={18} className="text-[#666]" />
          </button>
        </div>

        <div className="px-4 py-3 space-y-3">
          {error && <p data-testid="feed-composer-post-error" className="text-xs text-red-500">{error}</p>}
          {/* iter158 · Sichtbarer Hinweis wenn Composer im Poll-Modus geöffnet wurde */}
          {initialPollMode && (
            <div
              data-testid="feed-composer-poll-mode-banner"
              className="rounded-lg bg-[#0A0A0A] text-white px-3 py-2 text-xs font-medium flex items-center gap-2"
            >
              📊 Umfrage-Modus aktiv — Optionen unten ausfüllen
            </div>
          )}
          <div>
            {/* iter161 · Titel entfernt (im Feed nicht mehr sichtbar). Description = Pflicht. */}
            <label className="block text-[10px] uppercase tracking-wider text-[#999] mb-1">{t("feed.description_label", "Beschreibung *")}</label>
            <RichTextEditor
              data-testid="feed-composer-post-description"
              value={description}
              onChange={setDescription}
              placeholder={t("feed.composer_body_placeholder")}
              minHeight={140}
            />
            <AIAssistBar area={area === "STILLTOUCHEDX" ? "STX" : "SOCIAL"} value={description} onApply={setDescription} fieldType="caption" testIdSuffix="feed-desc" />
          </div>

          {/* iter134 / P4-UI: Monetization-Modus. Im Social-Bereich nur „Öffentlich" +
              „Einzelkauf" (Abo/PPV entfallen laut Bereichstrennung §3.3). In
              stilltouchedX alle 4 Modi. Backend erzwingt dies zusätzlich. */}
          <MonetizationModeSelector
            mode={monetizationMode}
            onModeChange={setMonetizationMode}
            tiers={visibleTiers}
            onTiersChange={setVisibleTiers}
            price={priceEur}
            onPriceChange={setPriceEur}
            allowedModes={area === "STILLTOUCHEDX" ? null : ["FREE", "SINGLE_BUY"]}
            testIdPrefix="feed-composer-monetization"          />
          {/* iter238 (Variante B §5) · Publish-Consent-Gate für PPV/SINGLE_BUY */}
          {(monetizationMode === "SINGLE_BUY" || monetizationMode === "PPV") && (
            <PublishConsentCheckbox
              checked={publishConsent}
              onChange={setPublishConsent}
              testId="feed-composer-publish-consent"
            />
          )}
          {/* iter134 / P4-UI: Listing-Attach — Social nur MARKETPLACE-Artikel,
              stilltouchedX nur eigene STX-Artikel (§3.6 / §7.6, serverseitig erzwungen). */}
          <ListingAttachPicker
            selectedIds={attachedListingIds}
            onChange={setAttachedListingIds}
            area={area === "STILLTOUCHEDX" ? "STILLTOUCHEDX" : "MARKETPLACE"}
            testIdPrefix="feed-composer-attach"
          />
          <div className="flex items-center gap-2 flex-wrap">
            {/* Ein einziger Kombi-Button: Desktop → Datei-Dialog, Mobil → native Auswahl Kamera/Galerie/Dateien */}
            <label className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E5E5E5] text-xs text-[#666] cursor-pointer hover:bg-[#F5F5F5]">
              <ImageIcon size={14} /> {t("feed.media_gallery_video_btn", "Bild/Video")}
              <input data-testid="feed-composer-post-file" type="file" accept="image/*,video/*" className="hidden" onChange={handleMediaUpload} disabled={uploading} />
            </label>
            {uploading && <UploadingBadge active testid="feed-composer-post-uploading" />}
            {mediaUrl && !uploading && (
              <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-[#F5F5F5] text-[10px] text-[#666]">
                {mediaType === "VIDEO" ? t("feed.video_attached", "Video angehängt") : t("feed.composer_image_attached")}
                <button data-testid="feed-composer-post-remove-media" onClick={() => { setMediaUrl(""); setMediaType("IMAGE"); setPendingFile(null); setEffects({ filter: "none", speed: 1, texts: [], stickers: [] }); setMusic(null); }} className="text-red-400 ml-1">
                  <Trash2 size={10} />
                </button>
              </div>
            )}
          </div>
          {/* iter161 · Sichtbarer Hinweis: was hochgeladen werden kann */}
          <p data-testid="feed-composer-post-media-hint" className="text-[10px] text-[#999] -mt-1">
            {t("feed.media_hint", { max: MAX_POST_VIDEO_SEC, defaultValue: `Bild oder Video hochladen · Videos max. ${MAX_POST_VIDEO_SEC}s (20 Min)` })}
          </p>
          <AnonymityNotice location="social_post" />
          {mediaUrl && !uploading && (
            <MediaEffectsEditor
              mediaUrl={mediaUrl}
              mediaType={mediaType}
              effects={effects}
              onEffectsChange={setEffects}
              allowMusic={false}
            />
          )}
          {pendingFile && !uploading && mediaType === "IMAGE" && (
            <MediaAnalyseChip
              file={pendingFile}
              onOptimizedFile={applyOptimizedFile}
              testIdSuffix="feed-post"
            />
          )}

          {/* iter150 (2026-02-24) · Umfrage-Panel */}
          <div ref={pollPanelRef} data-testid="feed-composer-poll-panel" className={`rounded-2xl border p-3 ${pollEnabled ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5]"}`}>
            <label className="flex items-center gap-2 text-sm font-medium text-[#0A0A0A] cursor-pointer">
              <input
                type="checkbox"
                data-testid="feed-composer-poll-toggle"
                checked={pollEnabled}
                onChange={(e) => {
                  const on = e.target.checked;
                  setPollEnabled(on);
                  if (on && pollOptions.length < 2) setPollOptions(["", ""]);
                  if (!on) setPollOptions([]);
                }}
                className="accent-[#0A0A0A]"
              />
              Umfrage anhängen
            </label>
            {pollEnabled && (
              <div className="mt-3 space-y-2">
                {pollOptions.map((opt, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      data-testid={`feed-composer-poll-option-${i}`}
                      value={opt}
                      onChange={(e) => {
                        const next = [...pollOptions];
                        next[i] = e.target.value;
                        setPollOptions(next);
                      }}
                      placeholder={`Antwort ${i + 1}`}
                      maxLength={80}
                      className="flex-1 h-9 px-3 rounded-full border border-[#E5E5E5] text-sm focus:outline-none focus:border-[#0A0A0A]"
                    />
                    {pollOptions.length > 2 && (
                      <button
                        type="button"
                        data-testid={`feed-composer-poll-remove-${i}`}
                        onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))}
                        className="h-9 w-9 rounded-full border border-[#E5E5E5] text-red-500 flex items-center justify-center hover:bg-red-50"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ))}
                {pollOptions.length < 6 && (
                  <button
                    type="button"
                    data-testid="feed-composer-poll-add-option"
                    onClick={() => setPollOptions([...pollOptions, ""])}
                    className="h-8 px-3 rounded-full border border-[#E5E5E5] text-xs font-medium text-[#0A0A0A] hover:bg-[#F5F5F5]"
                  >
                    + Antwort hinzufügen
                  </button>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <label className="text-[10px] uppercase tracking-wider text-[#999]">Laufzeit</label>
                  <select
                    data-testid="feed-composer-poll-duration"
                    value={pollDurationH}
                    onChange={(e) => setPollDurationH(Number(e.target.value))}
                    className="h-8 px-2 rounded-full border border-[#E5E5E5] text-xs bg-white"
                  >
                    <option value={6}>6 Stunden</option>
                    <option value={24}>{t("feed_composer.day_1")}</option>
                    <option value={72}>{t("feed_composer.day_3")}</option>
                    <option value={168}>{t("feed_composer.day_7")}</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 px-4 pb-4 pt-3 border-t border-[#E5E5E5] sticky bottom-0 bg-white">
          <button onClick={onClose} className="h-11 px-5 rounded-full border border-[#E5E5E5] text-sm text-[#666] hover:bg-[#FAFAFA]">{t("feed.composer_cancel")}</button>
          <button
            data-testid="feed-composer-post-publish"
            onClick={publish}
            disabled={publishing || uploading || (!description.replace(/<[^>]+>/g, "").trim() && !pollEnabled)}
            className="flex-1 rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1 disabled:opacity-30 hover:bg-[#222]"
          >
            <Send size={14} /> {publishing ? t("feed.composer_publishing") : t("feed.composer_publish")}
          </button>
        </div>
      </div>
    </div>
    {/* iter165 · Bild-Ausschnitt vor Upload (analog Profilbild) */}
    {cropFile && (
      <ImageCropDialog
        file={cropFile}
        aspect={16/9}
        title={t("feed_composer.crop_image")}
        onCancel={() => setCropFile(null)}
        onCropped={async (blob) => {
          setCropFile(null);
          setAnonPostFile(new File([blob], blob.name || "post.jpg", { type: blob.type || "image/jpeg" }));
        }}
      />
    )}
    {/* iter173f · Anonymisierungs-Engine vor dem Bild-Upload (Social-Beitrag).
        Confirm-Flow (kein returnBlob): der Server speichert das finale Bild UND hängt
        die auf dem ORIGINAL erkannte AVS-Info an die Datei → Full-Blur greift garantiert
        aufs Original, auch wenn der Nutzer mit dem Pinsel übermalt hat. */}
    {anonPostFile && (
      <AnonymizeDialog
        file={anonPostFile}
        context={area === "STILLTOUCHEDX" ? "STX_POST" : "SOCIAL_POST"}
        onCancel={() => setAnonPostFile(null)}
        onComplete={(url, data) => {
          setAnonPostFile(null);
          setMediaUrl(url);
          setMediaType("IMAGE");
          setPendingFile(null);
          if (data?.avs_body_content_present) setError(t("feed.body_content_detected"));
        }}
      />
    )}
  </>
  );
}

function StoryComposer({ onClose, onDone, area = "SOCIAL", editStory = null }) {
  const { t } = useTranslation();
  // iter80.6 Multi-Slide: max 20 slides (image+video mixed), video ≤30s per slide, ≤180s total.
  const MAX_SLIDES = 20;
  const MAX_VIDEO_SEC = 30;
  const MAX_TOTAL_VIDEO_SEC = 180;
  const isEdit = !!editStory;
  // iter320 · Bearbeiten: bestehende Slides vorbefüllen (Per-Element-Ablauf via created_at bewahren).
  const [slides, setSlides] = useState(() =>
    (editStory?.slides || []).map((s, i) => ({
      media_url: s.media_url,
      media_type: s.media_type || "IMAGE",
      duration_sec: s.duration_sec ?? null,
      caption: s.caption || "",
      filter: s.effects?.filter || "none",
      stickers: (s.effects?.stickers || []).map((st, k) => ({ id: st.id ?? (Date.now() + i * 100 + k), ...st })),
      music: s.music || null,
      created_at: s.created_at || null,
      body_content_present: s.body_content_present ?? null,
    }))
  );
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");

  const totalVideoSec = slides.filter((s) => s.media_type === "VIDEO").reduce((sum, s) => sum + (s.duration_sec || 0), 0);
  const roomForMore = slides.length < MAX_SLIDES;

  // Phase 3 · Per-Slide-Effekte (Filter + Sticker) + Musik-Modus (ein Track für alle / pro Bild)
  const [editIdx, setEditIdx] = useState(0);
  const [musicMode, setMusicMode] = useState((editStory?.slides || []).some((s) => s.music) ? "per_slide" : "all"); // "all" | "per_slide"
  const [storyMusic, setStoryMusic] = useState(editStory?.music || null); // gilt im "all"-Modus für ALLE Slides
  const [storyShowMusic, setStoryShowMusic] = useState(false);
  const [storyShowStickers, setStoryShowStickers] = useState(false);
  const [activeSticker, setActiveSticker] = useState(null);
  const stageRef = useRef(null);
  const dragRef = useRef(null);

  const curIdx = Math.min(editIdx, Math.max(0, slides.length - 1));
  const cur = slides[curIdx] || null;
  const curFilter = cur?.filter || "none";
  const curStickers = cur?.stickers || [];

  const patchSlide = (i, patch) => setSlides((prev) => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const setCurFilter = (f) => patchSlide(curIdx, { filter: f });
  const setCurMusic = (m) => patchSlide(curIdx, { music: m });

  const addSticker = (emoji) => {
    const n = curStickers.length;
    const ns = { id: Date.now() + Math.random(), emoji,
      x: Math.min(0.8, Math.max(0.2, 0.5 + ((n % 4) - 1.5) * 0.08)),
      y: Math.min(0.8, Math.max(0.2, 0.5 + (Math.floor(n / 4) - 1) * 0.08)), size: 56 };
    setSlides((prev) => prev.map((s, idx) => idx === curIdx ? { ...s, stickers: [...(s.stickers || []), ns] } : s));
    setActiveSticker(ns.id);
  };
  const updateSticker = (id, patch) => setSlides((prev) => prev.map((s, idx) => idx === curIdx
    ? { ...s, stickers: (s.stickers || []).map((st) => st.id === id ? { ...st, ...patch } : st) } : s));
  const removeSticker = (id) => { setSlides((prev) => prev.map((s, idx) => idx === curIdx
    ? { ...s, stickers: (s.stickers || []).filter((st) => st.id !== id) } : s)); setActiveSticker(null); };
  const onStickerDown = (id) => (ev) => { ev.stopPropagation(); dragRef.current = id; setActiveSticker(id); };
  const onStageMove = (ev) => {
    if (dragRef.current == null || !stageRef.current) return;
    const r = stageRef.current.getBoundingClientRect();
    const cx = (ev.touches ? ev.touches[0].clientX : ev.clientX) - r.left;
    const cy = (ev.touches ? ev.touches[0].clientY : ev.clientY) - r.top;
    updateSticker(dragRef.current, { x: Math.min(0.97, Math.max(0.03, cx / r.width)), y: Math.min(0.97, Math.max(0.03, cy / r.height)) });
  };
  const onStageUp = () => { dragRef.current = null; };

  const probeDuration = (file) => new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { const d = v.duration; window.URL.revokeObjectURL(v.src); resolve(d); };
    v.onerror = () => resolve(null);
    v.src = URL.createObjectURL(file);
  });

  const uploadOne = async (file, isVideo) => {
    if (slides.length >= MAX_SLIDES) {
      setError(`Max ${MAX_SLIDES} Slides pro Story.`);
      return;
    }
    setError(""); setUploading(true);
    try {
      let dur = null;
      if (isVideo) {
        dur = await probeDuration(file);
        if (dur == null) throw new Error(t("feed_composer.video_read_error"));
        if (dur > MAX_VIDEO_SEC + 0.5) {
          setError(`Video zu lang (${Math.round(dur)}s). Max ${MAX_VIDEO_SEC}s pro Video.`);
          return;
        }
        if (totalVideoSec + dur > MAX_TOTAL_VIDEO_SEC + 0.5) {
          setError(`Gesamt-Videolänge würde ${Math.round(totalVideoSec + dur)}s. Max ${MAX_TOTAL_VIDEO_SEC}s pro Story.`);
          return;
        }
      }
      const fd = new FormData();
      const toSend = isVideo ? file : await downscaleImage(file);
      fd.append("file", toSend);
      const endpoint = isVideo ? `/upload/video?area_scope=${area}` : `/upload?area_scope=${area}`;
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `/api/files/${data.path}`;
      // iter173b · Story-Slides BLOCKIEREN bei Body-Content (User-Vorgabe 2026-02-28):
      // Stories sollen keine AVS-Inhalte enthalten dürfen (wie MP-Upload-Block).
      // Feed-Post-Warning liegt in PostComposer (unten), nicht hier.
      if (!isVideo && data?.explicit_detected) {
        setError(t("feed.story_body_content_blocked"));
        // Note: Orphaned upload bleibt im Bucket (kein Cleanup-Endpoint) — Storage
        // ist Deduplicated und file wird nicht verlinkt, wenn Story-Slide nicht angelegt.
        return;
      }
      setSlides((prev) => [...prev, {
        media_url: fullUrl,
        media_type: isVideo ? "VIDEO" : "IMAGE",
        duration_sec: dur,
        caption: "",
        filter: "none",
        stickers: [],
        music: null,
      }]);
    } catch (err) {
      setError(formatError(err) || t("feed.upload_failed_retry", { defaultValue: "Upload fehlgeschlagen. Bitte erneut versuchen oder eine kleinere Datei (JPG/PNG/MP4) wählen." }));
    } finally { setUploading(false); }
  };

  const handleImagePick = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) await uploadOne(f, false);
    e.target.value = "";
  };
  const handleVideoPick = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) await uploadOne(f, true);
    e.target.value = "";
  };

  const removeSlide = (i) => {
    setSlides((prev) => prev.filter((_, idx) => idx !== i));
    setEditIdx((e) => {
      if (e > i) return e - 1;
      if (e === i) return Math.max(0, Math.min(e, slides.length - 2));
      return e;
    });
    setActiveSticker(null);
  };
  const moveSlide = (i, dir) => {
    const j = i + dir;
    setSlides((prev) => {
      const arr = [...prev];
      if (j < 0 || j >= arr.length) return arr;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
    setEditIdx((e) => {
      if (j < 0 || j >= slides.length) return e;
      if (e === i) return j;
      if (e === j) return i;
      return e;
    });
  };

  const publish = async () => {
    if (slides.length === 0) return;
    setError(""); setPublishing(true);
    try {
      const perSlide = musicMode === "per_slide";
      const payload = {
        area_scope: area,
        music: (!perSlide && storyMusic) ? storyMusic : undefined,
        slides: slides.map((s) => ({
          media_url: s.media_url,
          media_type: s.media_type,
          duration_sec: s.duration_sec,
          caption: (s.caption || "").trim() || undefined,
          effects: ((s.filter && s.filter !== "none") || (s.stickers || []).length > 0)
            ? { filter: s.filter || "none", speed: 1, texts: [], stickers: s.stickers || [] }
            : undefined,
          music: (perSlide && s.music) ? s.music : undefined,
          // iter320 · Per-Element-Ablauf: bestehende Slides senden created_at zurück,
          // damit ihre 24h ab Original-Upload weiterlaufen (neue Slides: undefined → jetzt).
          created_at: s.created_at || undefined,
          body_content_present: s.body_content_present ?? undefined,
        })),
      };
      if (isEdit) await api.put(`/studio/social/stories/${editStory.story_id}`, payload);
      else await api.post("/studio/social/stories", payload);
      onDone();
    } catch (err) {
      setError(formatError(err) || t("feed.composer_story_failed"));
    } finally { setPublishing(false); }
  };

  return (
    <div data-testid="feed-composer-story" className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl shadow-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
          <p className="text-sm font-semibold text-[#0A0A0A]">{isEdit ? t("feed.composer_story_edit_title", { defaultValue: "Story bearbeiten" }) : t("feed.composer_story_title")}</p>
          <button data-testid="feed-composer-story-close" onClick={onClose} aria-label={t("feed.composer_close")}
            className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-[#F5F5F5]">
            <X size={18} className="text-[#666]" />
          </button>
        </div>

        <div className="px-4 py-3 space-y-3">
          {error && <p data-testid="feed-composer-story-error" className="text-xs text-red-500">{error}</p>}

          {/* Phase 3 · Per-Slide-Effekte (Filter + Sticker) + Musik */}
          {slides.length > 0 && cur && (
            <div data-testid="story-effects-bar" className="space-y-2">
              {/* Slide-Auswahl: welches Bild wird gerade bearbeitet */}
              {slides.length > 1 && (
                <div className="flex gap-1.5 overflow-x-auto scrollbar-hide py-1">
                  {slides.map((s, i) => (
                    <button key={i} data-testid={`story-edit-slide-${i}`} onClick={() => { setEditIdx(i); setActiveSticker(null); }}
                      className={`relative shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 ${i === curIdx ? "border-[#0A0A0A]" : "border-transparent opacity-70"}`}>
                      {s.media_type === "VIDEO" ? (
                        <video src={s.media_url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                      ) : (
                        <img src={s.media_url} alt="" className="w-full h-full object-cover" />
                      )}
                      <span className="absolute top-0.5 left-0.5 text-[8px] bg-black/70 text-white px-1 rounded-full">{i + 1}</span>
                      {((s.stickers || []).length > 0 || (musicMode === "per_slide" && s.music)) && (
                        <span className="absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full bg-[#3B82F6]" />
                      )}
                    </button>
                  ))}
                </div>
              )}
              {slides.length > 1 && (
                <p data-testid="story-editing-slide-label" className="text-[10px] text-[#999] text-center">
                  {t("feed.story_editing_slide", { n: curIdx + 1, defaultValue: `Bild ${curIdx + 1} wird bearbeitet` })}
                </p>
              )}

              {/* Sticker-Bühne über dem aktuellen Slide */}
              <div className="flex justify-center">
              <div
                ref={stageRef}
                data-testid="story-sticker-stage"
                className="relative inline-block max-h-64 rounded-2xl overflow-hidden bg-black select-none"
                onMouseMove={onStageMove} onMouseUp={onStageUp} onMouseLeave={onStageUp}
                onTouchMove={onStageMove} onTouchEnd={onStageUp}
              >
                {cur.media_type === "VIDEO" ? (
                  <video key={cur.media_url} src={cur.media_url} muted playsInline preload="metadata"
                    className="block max-h-64 max-w-full w-auto object-contain" style={{ filter: filterCss(curFilter) }} />
                ) : (
                  <img key={cur.media_url} src={cur.media_url} alt="" className="block max-h-64 max-w-full w-auto object-contain" style={{ filter: filterCss(curFilter) }} />
                )}
                {curStickers.map((st) => (
                  <div key={st.id} data-testid={`story-sticker-${st.id}`}
                    onMouseDown={onStickerDown(st.id)} onTouchStart={onStickerDown(st.id)}
                    className={`absolute cursor-move leading-none select-none ${activeSticker === st.id ? "ring-1 ring-white/70 rounded" : ""}`}
                    style={{ left: `${st.x * 100}%`, top: `${st.y * 100}%`, transform: "translate(-50%,-50%)",
                      fontSize: `${st.size}px`, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))", touchAction: "none" }}>
                    {st.emoji}
                  </div>
                ))}
              </div>
              </div>
              {/* Filter für den aktuellen Slide */}
              <div className="flex gap-2 overflow-x-auto scrollbar-hide py-1">
                {FILTER_KEYS.map((k) => (
                  <button key={k} data-testid={`story-filter-${k}`} onClick={() => setCurFilter(k)}
                    className={`shrink-0 flex flex-col items-center gap-1 ${curFilter === k ? "opacity-100" : "opacity-70"}`}>
                    <div className="w-11 h-11 rounded-lg overflow-hidden border-2" style={{ borderColor: curFilter === k ? "#0A0A0A" : "transparent" }}>
                      {cur.media_type === "VIDEO" ? (
                        <div className="w-full h-full bg-[#333]" style={{ filter: filterCss(k) }} />
                      ) : (
                        <img src={cur.media_url} alt="" className="w-full h-full object-cover" style={{ filter: filterCss(k) }} />
                      )}
                    </div>
                    <span className="text-[9px] text-[#666]">{t(`effects.filter_${k}`, { defaultValue: FILTER_PRESETS[k].label })}</span>
                  </button>
                ))}
              </div>
              {/* Sticker-Auswahl (für den aktuellen Slide) */}
              <div className="flex items-center gap-2">
                {activeSticker != null && curStickers.find((s) => s.id === activeSticker) && (
                  <div className="flex items-center gap-2 flex-1 bg-[#F9F9F9] rounded-xl p-2">
                    <span className="text-lg leading-none">{curStickers.find((s) => s.id === activeSticker).emoji}</span>
                    <input data-testid="story-sticker-size" type="range" min={24} max={140}
                      value={curStickers.find((s) => s.id === activeSticker).size}
                      onChange={(ev) => updateSticker(activeSticker, { size: Number(ev.target.value) })}
                      className="flex-1 accent-[#0A0A0A]" />
                    <button data-testid="story-sticker-remove" onClick={() => removeSticker(activeSticker)} className="text-red-400"><X size={14} /></button>
                  </div>
                )}
                <button data-testid="story-sticker-toggle" onClick={() => setStoryShowStickers((v) => !v)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs hover:bg-[#F5F5F5] ${storyShowStickers ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-[#E5E5E5] text-[#666]"}`}>
                  <Smile size={13} /> {t("effects.stickers", { defaultValue: "Sticker" })}
                </button>
              </div>
              {storyShowStickers && (
                <div data-testid="story-sticker-picker" className="max-h-36 overflow-y-auto space-y-2 bg-[#FAFAFA] rounded-xl p-2">
                  {STICKER_GROUPS.map((g) => (
                    <div key={g.key}>
                      <p className="text-[9px] uppercase tracking-wider text-[#AAA] mb-1">{t(`effects.sticker_${g.key}`, { defaultValue: g.key })}</p>
                      <div className="flex flex-wrap gap-1">
                        {g.emojis.map((emoji) => (
                          <button key={emoji} data-testid={`story-sticker-add-${emoji}`} onClick={() => addSticker(emoji)}
                            className="w-9 h-9 flex items-center justify-center text-xl rounded-lg hover:bg-[#F0F0F0] active:scale-90 transition-transform">
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Musik — Modus wählen (ein Track für alle / pro Bild) */}
              <div className="space-y-2 pt-2 border-t border-[#F0F0F0]">
                {slides.length > 1 && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-[#AAA]">{t("effects.music", { defaultValue: "Musik" })}</span>
                    <div className="flex gap-1 ml-auto">
                      <button data-testid="story-music-mode-all" onClick={() => setMusicMode("all")}
                        className={`px-2.5 py-1 rounded-full text-[10px] font-medium ${musicMode === "all" ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#666]"}`}>
                        {t("feed.music_mode_all", { defaultValue: "Ein Track für alle" })}
                      </button>
                      <button data-testid="story-music-mode-per" onClick={() => setMusicMode("per_slide")}
                        className={`px-2.5 py-1 rounded-full text-[10px] font-medium ${musicMode === "per_slide" ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#666]"}`}>
                        {t("feed.music_mode_per", { defaultValue: "Pro Bild" })}
                      </button>
                    </div>
                  </div>
                )}
                {(() => {
                  const perSlide = musicMode === "per_slide";
                  const track = perSlide ? cur.music : storyMusic;
                  const setTrack = perSlide ? setCurMusic : setStoryMusic;
                  return track ? (
                    <div data-testid="story-music-chip" className="flex items-center gap-2 bg-[#F0F7FF] border border-[#DBEAFE] rounded-xl px-3 py-2">
                      <Music2 size={14} className="text-[#3B82F6]" />
                      <span className="flex-1 text-xs text-[#0A0A0A] truncate">
                        {track.title}{perSlide ? ` · ${t("feed.story_slide_short", { n: curIdx + 1, defaultValue: `Bild ${curIdx + 1}` })}` : ""}
                      </span>
                      <button data-testid="story-music-remove" onClick={() => setTrack(null)} className="text-red-400"><X size={14} /></button>
                    </div>
                  ) : (
                    <button data-testid="story-music-add" onClick={() => setStoryShowMusic(true)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E5E5E5] text-xs text-[#666] hover:bg-[#F5F5F5]">
                      <Music2 size={13} /> {perSlide
                        ? t("feed.music_add_for_slide", { n: curIdx + 1, defaultValue: `Musik für Bild ${curIdx + 1}` })
                        : t("effects.music", { defaultValue: "Musik" })}
                    </button>
                  );
                })()}
              </div>
              {storyShowMusic && (
                <MusicPicker selectedId={musicMode === "per_slide" ? cur.music?.id : storyMusic?.id} onClose={() => setStoryShowMusic(false)}
                  onSelect={(tr) => {
                    const trk = { id: tr.id, title: tr.title, artist: tr.artist, preview_url: tr.preview_url, source: tr.source };
                    if (musicMode === "per_slide") setCurMusic(trk); else setStoryMusic(trk);
                  }} />
              )}
            </div>
          )}

          {/* Slides-Vorschau mit Per-Slide-Beschreibung */}
          {slides.length > 0 && (
            <div data-testid="feed-composer-story-slides" className="space-y-3">
              {slides.map((s, i) => (
                <div key={i} data-testid={`feed-composer-story-slide-${i}`} className="rounded-2xl border border-[#E5E5E5] p-2 space-y-2">
                  <div className="flex gap-2">
                    <div className="relative w-20 h-20 shrink-0 bg-[#0A0A0A] rounded-xl overflow-hidden">
                      {s.media_type === "VIDEO" ? (
                        <video src={s.media_url} className="w-full h-full object-cover" style={{ filter: filterCss(s.filter || "none") }} muted playsInline preload="metadata" />
                      ) : (
                        <img src={s.media_url} alt="" className="w-full h-full object-cover" style={{ filter: filterCss(s.filter || "none") }} />
                      )}
                      <span className="absolute top-1 left-1 text-[9px] bg-black/70 text-white px-1 rounded-full">
                        {i + 1}{s.media_type === "VIDEO" ? ` · ${Math.round(s.duration_sec)}s` : ""}
                      </span>
                      {(s.caption || "").length > 0 && (
                        <div data-testid={`feed-composer-story-slide-overlay-${i}`}
                          className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[9px] px-1 py-0.5 leading-tight line-clamp-2">
                          {s.caption}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <textarea
                        data-testid={`feed-composer-story-slide-caption-${i}`}
                        value={s.caption || ""}
                        onChange={(e) => setSlides((prev) => prev.map((x, idx) => idx === i ? { ...x, caption: e.target.value.slice(0, 120) } : x))}
                        placeholder={t("feed.composer_slide_desc_placeholder")}
                        rows={2}
                        maxLength={120}
                        className="w-full text-[11px] px-3 py-2 rounded-xl border border-[#E5E5E5] focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] resize-none"
                      />
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1">
                          <button
                            data-testid={`feed-composer-story-slide-up-${i}`}
                            onClick={() => moveSlide(i, -1)} disabled={i === 0}
                            className="text-[#666] text-xs w-7 h-7 rounded-full border border-[#E5E5E5] disabled:opacity-30 hover:bg-[#FAFAFA]">◄</button>
                          <button
                            data-testid={`feed-composer-story-slide-down-${i}`}
                            onClick={() => moveSlide(i, +1)} disabled={i === slides.length - 1}
                            className="text-[#666] text-xs w-7 h-7 rounded-full border border-[#E5E5E5] disabled:opacity-30 hover:bg-[#FAFAFA]">►</button>
                        </div>
                        <button data-testid={`feed-composer-story-slide-remove-${i}`}
                          onClick={() => removeSlide(i)}
                          className="text-red-500 text-xs inline-flex items-center gap-1 rounded-full px-2 py-1 hover:bg-red-50">
                          <Trash2 size={11} /> {t("feed.remove")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upload-Buttons */}
          {roomForMore && (
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-1 justify-center px-3 py-2 rounded-full border border-[#E5E5E5] text-xs text-[#666] cursor-pointer hover:bg-[#F5F5F5]">
                <ImageIcon size={14} /> {t("feed.composer_add_images")}
                <input data-testid="feed-composer-story-file" type="file" accept="image/*" multiple className="hidden" onChange={handleImagePick} disabled={uploading} />
              </label>
              <label className="flex items-center gap-1 justify-center px-3 py-2 rounded-full border border-[#E5E5E5] text-xs text-[#666] cursor-pointer hover:bg-[#F5F5F5]">
                <Film size={14} /> {t("feed.composer_add_video", "Video")}
                <input data-testid="feed-composer-story-video" type="file" accept="video/*" multiple className="hidden" onChange={handleVideoPick} disabled={uploading} />
              </label>
            </div>
          )}

          <input
            data-testid="feed-composer-story-caption-hidden"
            type="hidden"
            value=""
            readOnly
          />

          <div className="text-[10px] text-[#999] space-y-1">
            <p>{t("feed.composer_slides_meta", { current: slides.length, max: MAX_SLIDES, videoSec: Math.round(totalVideoSec), maxTotal: MAX_TOTAL_VIDEO_SEC, maxPerVid: MAX_VIDEO_SEC })}</p>
            {isEdit ? (
              <p className="text-[#666]">{t("feed.composer_story_edit_tip", { defaultValue: "Jedes Bild/Video bleibt ab seinem eigenen Upload 24 Std. sichtbar. Neue Elemente starten jetzt, ältere laufen unverändert weiter." })}</p>
            ) : (
              <>
                <p>{t("feed.composer_story_visibility")}</p>
                <p className="text-[#666]">{t("feed.composer_story_tip")}</p>
              </>
            )}
            <UploadingBadge active={uploading} testid="feed-composer-story-uploading" />
          </div>
        </div>

        <div className="flex gap-2 px-4 pb-4 pt-3 border-t border-[#E5E5E5] sticky bottom-0 bg-white">
          <button onClick={onClose} className="h-11 px-5 rounded-full border border-[#E5E5E5] text-sm text-[#666] hover:bg-[#FAFAFA]">{t("feed.composer_cancel")}</button>
          <button
            data-testid="feed-composer-story-publish"
            onClick={publish}
            disabled={publishing || uploading || slides.length === 0}
            className="flex-1 rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1 disabled:opacity-30 hover:bg-[#222]"
          >
            <Send size={14} /> {publishing ? t("feed.composer_publishing") : (isEdit ? t("feed.composer_story_save", { defaultValue: "Speichern" }) : t("feed.composer_story_publish", { count: slides.length }))}
          </button>
        </div>
      </div>
    </div>
  );
}
